import { useState, useEffect, useRef, } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Helmet } from 'react-helmet-async';
import 'leaflet/dist/leaflet.css';
import Sidebar from '../components/shared/Sidebar';
import ParameterModal from '../components/shared/ParameterModal';
import { MapTheme, MapView } from '../components/map/types';
import ViewToggle from '../components/map/ViewToggle';
import ThemeSelector from '../components/map/ThemeSelector';
import LiveSiteDetails from '../components/shared/LiveSiteDetails';
import { ApiLocation, fetchLocations } from '../services/api';
import { getMockLocations } from '../services/mockData';
import L from 'leaflet';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import LoadingIndicator from '../components/shared/LoadingIndicator';
import { RefreshCw, Plus, Minus } from 'lucide-react';
import { buoys } from '../data/buoy';
import { LocationType } from '../services/api';
import BuoyDetails from '../components/shared/BuoyDetails';

// Fix for marker icon issue in Leaflet with React
// This needs to be done once in the application
const fixLeafletIcon = () => {
  // @ts-ignore - Leaflet types are not complete
  delete L.Icon.Default.prototype._getIconUrl;
  
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  });
};

const iconSizeDefault: [number, number] = [25, 41];
const iconAnchorDefault: [number, number] = [12, 41];

const iconSizeSelected: [number, number] = [30, 46];
const iconAnchorSelected: [number, number] = [12, 46];

const buoyIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: iconSizeDefault,
  iconAnchor: iconAnchorDefault,
});

const selectedBuoyIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: iconSizeSelected,
  iconAnchor: iconAnchorSelected,
});

const sensorIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: iconSizeDefault,
  iconAnchor: iconAnchorDefault,
});

const selectedSensorIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: iconSizeSelected,
  iconAnchor: iconAnchorSelected,
});

// Add this component for map control
// NOTE: Only recenter when the *selected site* actually changes — Map.tsx
// re-renders often (theme changes, sidebar toggling, polling, etc.), and
// calling setView() during render on every one of those would snap the map
// back to the marker mid-pan/zoom, which made the map feel "stuck" (most
// noticeable on mobile where that's the only way to look around).
function MapController({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng], 13);
  }, [lat, lng, map]);
  return null;
}

// Define map styles for different themes
const mapStyles = {
  streets: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
  },
  minimal: {
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  }
};

// Create a component to handle theme changes
function ThemeController({ theme }: { theme: MapTheme }) {
  return (
    <TileLayer
      url={mapStyles[theme].url}
      attribution={mapStyles[theme].attribution}
    />
  );
}

export default function Map() {
  // Fix Leaflet icon issue when component mounts
  useEffect(() => {
    fixLeafletIcon();
  }, []);

  const [locations, setLocations] = useState<ApiLocation[]>([]);
  const [visibleLocations, setVisibleLocations] = useState<ApiLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<ApiLocation | null>(null);
  const [selectedParameter, setSelectedParameter] = useState<null | any>(null);
  const [theme, setTheme] = useState<MapTheme>('streets');
  const [view, setView] = useState<MapView>('2D');
  const [manualRefreshKey, setManualRefreshKey] = useState<number>(0);
  const [showBuoys, setShowBuoys] = useState(false);
  const prevShowBuoysRef = useRef<boolean | null>(null);  
  const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < 640;
  // Default the details drawer to closed on small screens (it becomes a
  // full-width overlay there — see Sidebar.tsx) so the map itself, not the
  // site info panel, is what mobile users see and can pan/zoom on first load.
  const [sideBarIsOpen, setSideBarIsOpen] = useState(() => !isMobileViewport());
  const [locationsWarning, setLocationsWarning] = useState<string | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  // Re-evaluate the mobile/desktop drawer default when the viewport crosses
  // the breakpoint (device rotation, resizing a browser window, etc.) so the
  // map doesn't stay hidden behind a full-width drawer after rotating.
  useEffect(() => {
    const wasMobile = { current: isMobileViewport() };
    const handleResize = () => {
      const mobileNow = isMobileViewport();
      if (mobileNow !== wasMobile.current) {
        wasMobile.current = mobileNow;
        setSideBarIsOpen(!mobileNow);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const USE_MOCK_LOCATIONS = false; // Set to true to skip live API and use static mock locations

  debug("locations", locations, visibleLocations)
  const buoySelected = selectedLocation?.type == LocationType.Buoy;
debug("selected parameter", selectedParameter)

  // Fetch locations when component mounts
  useEffect(() => {
    let isMounted = true;
    
    async function loadLocations() {
      try {
        setIsLoading(true);
        setError(null);
        setLocationsWarning(null);

        let data: ApiLocation[];
        if (USE_MOCK_LOCATIONS) {
          data = getMockLocations();
        } else {
          try {
            data = await fetchLocations();
          } catch (fetchErr) {
            console.error('Live locations unavailable, using mock fallback:', fetchErr);
            data = getMockLocations();
            setLocationsWarning('Live locations unavailable — showing cached sites.');
          }
        }

        if (isMounted && data.length > 0) {
          setLocations([...data, ...buoys]);
          setSelectedLocation(data[0]);
          setVisibleLocations(data);
          setIsLoading(false);
        } else if (isMounted) {
          setError('No monitoring locations found.');
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching locations:', err);
        if (isMounted) {
          setError('Failed to load monitoring locations. Please try again later.');
          setIsLoading(false);
        }
      }
    }
    
    loadLocations();
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (prevShowBuoysRef.current == true && showBuoys == false && selectedLocation?.type == LocationType.Buoy) {
      setSelectedLocation(visibleLocations[0]);
    }
    prevShowBuoysRef.current = showBuoys;
  }, [showBuoys, selectedLocation]);

  const handleThemeChange = (newTheme: MapTheme) => {
    debug('Changing map theme to:', newTheme);
    setTheme(newTheme);
  };

  const handleViewChange = (newView: MapView) => {
    debug('View change clicked:', newView);
    setView(newView);
  };

  // Function to trigger data refresh
  const handleDataRefresh = () => {
    setManualRefreshKey(prev => prev + 1);
  };

  // Convert API location to map coordinates [lat, lng]
  const getCoordinates = (location: ApiLocation): [number, number] => {
    return [location.gCoord.x, location.gCoord.y];
  };

  useEffect(() => {
    setVisibleLocations(locations.filter(loc => (
      showBuoys ? true : loc.type !== LocationType.Buoy
    )))
  }, [locations, showBuoys])

  // Error fallback component
  const ErrorFallback = (
    <div className="h-[calc(100vh-8rem)] w-full flex items-center justify-center bg-[#E6F7F4]/30 backdrop-blur-sm">
      <div className="text-center p-6 max-w-md bg-white/80 rounded-lg shadow-md">
        <h3 className="text-xl font-bold text-red-600 mb-2">Map Error</h3>
        <p className="text-gray-700 mb-4">
          We encountered an error loading the map. Please try again later.
        </p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-sensor text-white rounded-lg hover:bg-[#004957] transition-colors"
        >
          Reload
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="h-[calc(100vh-8rem)] w-full flex items-center justify-center bg-[#E6F7F4]/30 backdrop-blur-sm">
        <div className="flex flex-col items-center bg-white/80 p-8 rounded-lg shadow-md">
          <LoadingIndicator />
          <p className="text-sensor text-lg mt-4">Loading map data...</p>
        </div>
      </div>
    );
  }

  if (error || !selectedLocation) {
    return (
      <div className="h-[calc(100vh-8rem)] w-full flex items-center justify-center bg-[#E6F7F4]/30 backdrop-blur-sm">
        <div className="text-center p-6 max-w-md bg-white/80 rounded-lg shadow-md">
          <p className="text-red-500 mb-4">{error || 'An error occurred loading the map'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-sensor text-white rounded-lg hover:bg-[#004957] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <div className="h-[calc(100vh-8rem)] w-full bg-[#E6F7F4]/30 backdrop-blur-sm">
        <Helmet>
          <title>Interactive Map | BIOCAP Website</title>
          <meta
            name="description"
            content="Explore BIOCAP's interactive map showing coastal protection sites and environmental data across different locations."
          />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        </Helmet>

        {/* Location Chips */}
        <div className="absolute top-4 left-4 z-[1000] flex flex-col gap-2">
          {locationsWarning && (
            <p className="px-3 py-1.5 rounded-md bg-amber-100/90 text-amber-900 text-xs shadow-sm max-w-sm">
              {locationsWarning}
            </p>
          )}
          <div className="flex gap-2 flex-wrap">
            {visibleLocations.map((location) => (
              <button
                key={location.ID}
                onClick={() => setSelectedLocation(location)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all shadow-sm hover:shadow-md
                  ${
                    selectedLocation.ID === location.ID
                      ? `${
                          location.type == LocationType.Sensor
                            ? "bg-sensor"
                            : "bg-buoy"
                        } text-white`
                      : `bg-white/70 backdrop-blur-sm border hover:bg-white/90 ${
                          location.type == LocationType.Sensor
                            ? "border-sensor/50 text-sensor"
                            : "border-buoy text-buoy"
                        }`
                  }`}
              >
                {location.siteName}
              </button>
            ))}
          </div>
        </div>

        {/* MAP */}
        <MapContainer
          ref={mapRef}
          center={getCoordinates(selectedLocation)}
          zoom={13}
          className="h-full w-full rounded-lg overflow-hidden bg-[#F8FAFB]/70 outline-none"
          zoomControl={false}
        >
          <MapController lat={selectedLocation.gCoord.x} lng={selectedLocation.gCoord.y} />
          <ThemeController theme={theme} />

          {visibleLocations.map((location) => {
            const selected = selectedLocation.ID === location.ID;

            return (
              <Marker
                key={location.ID}
                position={getCoordinates(location)}
                eventHandlers={{
                  click: () => setSelectedLocation(location),
                }}
                icon={
                  location.type == LocationType.Sensor
                    ? selected
                      ? selectedSensorIcon
                      : sensorIcon
                    : selected
                    ? selectedBuoyIcon
                    : buoyIcon
                }
              />
            );
          })}
        </MapContainer>

        {/* MAP CONTROLS - bottom left */}
        <div className="absolute left-4 bottom-6 z-[1000] flex flex-col gap-2 bg-white/60 backdrop-blur-sm p-3 rounded-lg shadow-md">
          <ViewToggle value={view} onChange={handleViewChange} />
          <ThemeSelector value={theme} onChange={handleThemeChange} />
          {/* Visible zoom affordance — pinch/drag work natively, but touch
              users (especially on mobile, where this was previously the
              only way to look around) benefit from explicit +/- buttons. */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm p-1 flex gap-1">
            <button
              onClick={() => mapRef.current?.zoomIn()}
              className="p-2 rounded transition-colors text-gray-600 hover:text-gray-900"
              title="Zoom in"
              aria-label="Zoom in"
            >
              <Plus className="h-4 w-4" />
            </button>
            <button
              onClick={() => mapRef.current?.zoomOut()}
              className="p-2 rounded transition-colors text-gray-600 hover:text-gray-900"
              title="Zoom out"
              aria-label="Zoom out"
            >
              <Minus className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* RIGHT CONTROLS (still overlaying map) */}
        <div
          className={`absolute bottom-6 right-4 z-[1000] flex flex-col gap-2 
            bg-white/60 backdrop-blur-sm p-3 rounded-lg shadow-md justify-center items-center
            transition-transform duration-300 ease-in-out 
            ${sideBarIsOpen ? '-translate-x-[100vw] sm:-translate-x-[27rem]' : 'translate-x-0'}
          `}>          
          {selectedLocation && (
            <button
              onClick={handleDataRefresh}
              className="mt-1 flex items-center gap-2 px-4 py-2 bg-white text-gray-700 shadow-sm rounded text-sm font-medium hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              Refresh Data
              <RefreshCw size={16} className="text-sensor mr-2" />

            </button>
          )}

          <button
            onClick={() => setShowBuoys(!showBuoys)}
            className="px-4 py-2 w-full text-sm justify-center font-medium hover:bg-gray-100 hover:text-gray-900 flex shadow-sm rounded transition-colors duration-300 bg-white"
          >
            <div className="flex justify-center text-center items-center gap-2">
              <p className="text-gray-700">Buoys</p>

              <div
                className={`w-11 h-[1.35rem] flex items-center rounded transition-colors duration-100 border border-grey/80
                  ${showBuoys ? "bg-buoy" : "bg-gray-300"}`}
              >
                <div
                  className={`h-4 w-4 rounded shadow-md transform transition-transform duration-300
                    ${
                      showBuoys
                        ? "translate-x-6 bg-white"
                        : "translate-x-0.5 bg-white"
                    }`}
                />
              </div>
            </div>
          </button>
        
        </div>

        <ErrorBoundary>
          <Sidebar
            isOpen={sideBarIsOpen}
            setIsOpen={setSideBarIsOpen}
            data={selectedLocation}
            mapTheme={theme}
            onMapThemeChange={handleThemeChange}
            mapView={view}
            onMapViewChange={handleViewChange}
          >
            {!buoySelected ? (
              <LiveSiteDetails
                location={selectedLocation}
                refreshTrigger={manualRefreshKey}
                onParameterClick={setSelectedParameter}
              />
            ) : (
              <BuoyDetails location={selectedLocation} />
            )}
          </Sidebar>
        </ErrorBoundary>

      </div>

        {/* MODAL */}
        <ParameterModal
          isOpen={!!selectedParameter}
          onClose={() => setSelectedParameter(null)}
          parameter={selectedParameter}
        />
    </ErrorBoundary>
  );
}